import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Plane, Loader2, RefreshCw, Download, AlertTriangle, IndianRupee,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports`;

const DATE_PRESETS = [
  { id: '7d', label: 'Last 7d' },
  { id: '30d', label: 'Last 30d' },
  { id: 'mtd', label: 'MTD' },
  { id: 'custom', label: 'Custom' },
];

const resolvePreset = (preset) => {
  const now = new Date();
  const toStr = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case '7d': {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return { from: toStr(d), to: toStr(now) };
    }
    case '30d': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return { from: toStr(d), to: toStr(now) };
    }
    case 'mtd':
      return { from: toStr(new Date(now.getFullYear(), now.getMonth(), 1)), to: toStr(now) };
    default:
      return null;
  }
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const SupplierTripjackReport = () => {
  const [preset, setPreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const range = preset === 'custom' ? { from: customFrom, to: customTo } : resolvePreset(preset);
      const r = await axios.post(
        `${API}/generate`,
        {
          report_type: 'supplier_tripjack',
          date_from: range?.from,
          date_to: range?.to,
          group_by: groupBy,
          limit: 500,
        },
        { withCredentials: true },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [preset, customFrom, customTo, groupBy]);

  useEffect(() => { run(); }, [run]);

  const exportCsv = async () => {
    const range = preset === 'custom' ? { from: customFrom, to: customTo } : resolvePreset(preset);
    const r = await axios.post(
      `${API}/export`,
      {
        report_type: 'supplier_tripjack',
        date_from: range?.from,
        date_to: range?.to,
        group_by: groupBy,
        export_format: 'csv',
        limit: 500,
      },
      { withCredentials: true, responseType: 'blob' },
    );
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `tripjack_supplier_${range?.from || 'report'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const summary = data?.summary || {};
  const balance = data?.live_balance || {};
  const series = data?.series || [];
  const bookings = data?.bookings || [];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="supplier-tripjack-report">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plane className="w-7 h-7 text-teal-600" />
            TripJack Supplier Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Agent TripJack sales, net supplier cost (NF), and live credit outstanding.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={loading || !data}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="supplier-tripjack-export"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={run}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            data-testid="supplier-tripjack-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Outstanding balance is live from TripJack API. Payment due dates are not provided via API.
          Daily digest email is sent at 08:00 IST to the admin team.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              preset === p.id ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              className="px-2 py-1 text-xs border rounded" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              className="px-2 py-1 text-xs border rounded" />
          </>
        )}
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="ml-auto px-2 py-1.5 text-xs border rounded-lg"
        >
          <option value="day">By day</option>
          <option value="week">By week</option>
          <option value="month">By month</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading report…
        </div>
      )}

      {data && (
        <>
          <p className="text-xs text-gray-500 mb-3">{data.period_label}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Period GMV', value: fmt(summary.total_gmv) },
              { label: 'Net Paid (NF)', value: fmt(summary.period_net_paid) },
              { label: 'Bookings', value: summary.total_bookings ?? 0 },
              { label: 'Live Outstanding', value: balance.success ? fmt(balance.total_outstanding) : 'N/A' },
            ].map((card) => (
              <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-[10px] uppercase font-semibold text-gray-500">{card.label}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{card.value}</p>
              </div>
            ))}
          </div>

          {balance.success && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap gap-6 text-sm">
              <span><IndianRupee className="w-4 h-4 inline text-teal-600" /> Spendable: <b>{fmt(balance.spendable_balance)}</b></span>
              <span>Wallet: <b>{fmt(balance.wallet_balance)}</b></span>
              <span>Credit: <b>{fmt(balance.credit_balance)}</b></span>
              {summary.recon_alerts_open > 0 && (
                <span className="text-amber-700">Open recon alerts: <b>{summary.recon_alerts_open}</b></span>
              )}
            </div>
          )}

          {series.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3">Net supplier cost over period</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="net_cost" fill="#0d9488" name="Net cost (NF)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-800">Booking drill-down</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">PNR</th>
                    <th className="px-4 py-2">Agent</th>
                    <th className="px-4 py-2 text-right">Ticket Cost</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2">Booking ID</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No bookings in this period</td>
                    </tr>
                  ) : (
                    bookings.map((row) => (
                      <tr key={row.booking_id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-xs">{row.pnr}</td>
                        <td className="px-4 py-2">{row.agent_name}</td>
                        <td className="px-4 py-2 text-right">{row.ticket_cost_fmt ? `₹${row.ticket_cost_fmt}` : fmt(row.ticket_cost)}</td>
                        <td className="px-4 py-2 text-right">{row.total_fmt ? `₹${row.total_fmt}` : fmt(row.total)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{row.booking_id}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SupplierTripjackReport;
